import {PICKING_LIST_BATCHES_CATEGORIES} from '@/modules/production/constants'
import {PickingListOrder, PickingListOrderCategoryBatch} from '@cypro/engine/database/schema'
import {Batch} from '@cypro/engine/schema'

export type PickingListBatchCategory = (typeof PICKING_LIST_BATCHES_CATEGORIES)[number]

export function mapPickingListBatchesIds(batches: PickingListOrderCategoryBatch[] | undefined) {
  return batches?.map(b => b.batchId) || []
}

/**
 * Creates a `Map<batchId, quantity>` for a single picking-list category. The same batch
 * can be allocated to several categories (e.g. a carrier solution doubling as the
 * reconstitution solvent), so quantities must be scoped per category rather than summed
 * across all of them.
 */
export function createPickingListBatchQuantityMap(
  sequenceOrder: PickingListOrder | undefined,
  category: PickingListBatchCategory
): Map<string, number> {
  const map = new Map<string, number>()

  if (!sequenceOrder) return map

  for (const batch of sequenceOrder[category]?.batches ?? []) {
    if (batch.batchId) {
      map.set(batch.batchId, (map.get(batch.batchId) ?? 0) + batch.quantity)
    }
  }

  return map
}

export function mapPickingListAllocatedQuantityToBatches(
  batches: Batch[],
  quantityMap: ReturnType<typeof createPickingListBatchQuantityMap>,
  sharedEntries: readonly {batchId: string; sharedWith: string[]; sharedDose?: number}[] = []
) {
  const sharedByBatch = new Map(sharedEntries.map(entry => [entry.batchId, entry]))
  return batches.map(batch => {
    const shared = sharedByBatch.get(batch.id)
    return {
      ...batch,
      allocatedPickingListQuantity: quantityMap.get(batch.id),
      ...(shared ? {sharedWith: shared.sharedWith, sharedDose: shared.sharedDose} : {}),
    }
  })
}

interface DistributedOrderBatches {
  activeSubstances?: {batches: readonly {batchId: string}[]} | null
  carrier?: {batches: readonly {batchId: string}[]} | null
  solvents?: {batches: readonly {batchId: string}[]} | null
  consumables?: {batches: readonly {batchId: string}[]} | null
  restVials?: readonly {batchId?: string | null}[] | null
}

/** Every batch a prescription's picking list used: drug, carrier, solvent, consumable and rest vial. */
export function getDistributedOrderBatchIds(item: DistributedOrderBatches | undefined | null): string[] {
  if (!item) return []
  return [
    ...(item.activeSubstances?.batches.map(batch => batch.batchId) ?? []),
    ...(item.carrier?.batches.map(batch => batch.batchId) ?? []),
    ...(item.solvents?.batches.map(batch => batch.batchId) ?? []),
    ...(item.consumables?.batches.map(batch => batch.batchId) ?? []),
    ...(item.restVials?.flatMap(restVial => (restVial.batchId ? [restVial.batchId] : [])) ?? []),
  ]
}
